import {
  CyberAssessmentActionPlanItemSelectDto,
  CyberAssessmentActionPlanSelectDto,
  CyberAssessmentAukusBlockerSelectDto,
  CyberAssessmentAukusGateResultSelectDto,
  CyberAssessmentAukusPillar2SelectDto,
  CyberAssessmentBlockingDeficienciesSelectDto,
  CyberAssessmentBlockingDeficiencyItemSelectDto,
  CyberAssessmentBlockingType,
  CyberAssessmentCmmcDomainSummarySelectDto,
  CyberAssessmentCmmcGapPriorityGroupSelectDto,
  CyberAssessmentCmmcGapPrioritySelectDto,
  CyberAssessmentCmmcSelectDto,
  CyberAssessmentEvidencePackChecklistItemSelectDto,
  CyberAssessmentEvidencePackChecklistJurisdictionSelectDto,
  CyberAssessmentEvidencePackChecklistSelectDto,
  CyberAssessmentJurisdictionQuestionSelectDto,
  CyberAssessmentJurisdictionReadinessSelectDto,
  CyberAssessmentOverviewSnapshotSelectDto,
  CyberAssessmentPurpleTeamExerciseSelectDto,
  CyberAssessmentReadinessComparisonSelectDto,
  CyberAssessmentReportSnapshotSelectDto,
} from '@@types/api.types';

type CyberAssessmentAukusGateLike = CyberAssessmentAukusGateResultSelectDto;

type CyberAssessmentAukusBlockerLike = Partial<CyberAssessmentAukusBlockerSelectDto> &
  Pick<CyberAssessmentAukusBlockerSelectDto, 'gateTags' | 'questionId' | 'questionSummary'>;

type CyberAssessmentCmmcDomainSummaryLike = CyberAssessmentCmmcDomainSummarySelectDto;

type CyberAssessmentCmmcGapPriorityLike = CyberAssessmentCmmcGapPrioritySelectDto;

type CyberAssessmentCmmcGapPriorityGroupLike = CyberAssessmentCmmcGapPriorityGroupSelectDto;

type CyberAssessmentJurisdictionQuestionLike = CyberAssessmentJurisdictionQuestionSelectDto;

type CyberAssessmentJurisdictionReadinessLike = Partial<CyberAssessmentJurisdictionReadinessSelectDto> &
  Pick<
    CyberAssessmentJurisdictionReadinessSelectDto,
    | 'amberQuestions'
    | 'atoPathway'
    | 'blockingQuestions'
    | 'classificationCeiling'
    | 'classificationCeilingRationale'
    | 'deploymentReadinessBand'
    | 'evidenceChecklist'
    | 'estimatedRemediationEffort'
    | 'jurisdiction'
    | 'keyControls'
    | 'keyMissingEvidence'
    | 'notes'
  >;

type CyberAssessmentEvidencePackChecklistItemLike = CyberAssessmentEvidencePackChecklistItemSelectDto;

type CyberAssessmentEvidencePackChecklistJurisdictionLike = CyberAssessmentEvidencePackChecklistJurisdictionSelectDto;

type CyberAssessmentLegacyCollection<TItem> = { items: TItem[] } | TItem[];

type CyberAssessmentReportSnapshotLike = Omit<
  CyberAssessmentReportSnapshotSelectDto,
  | 'actionPlan'
  | 'aukusPillar2'
  | 'blockingDeficiencies'
  | 'cmmc'
  | 'overview'
  | 'purpleTeamExercises'
  | 'readinessComparison'
> & {
  actionPlan: CyberAssessmentActionPlanSelectDto | CyberAssessmentLegacyCollection<CyberAssessmentActionPlanItemLike>;
  aukusPillar2:
    | CyberAssessmentAukusPillar2SelectDto
    | {
        blockerCount: number;
        summary: string;
      };
  blockingDeficiencies:
    | CyberAssessmentBlockingDeficienciesSelectDto
    | CyberAssessmentLegacyCollection<CyberAssessmentBlockingItemLike>;
  cmmc:
    | CyberAssessmentCmmcSelectDto
    | {
        summary: string;
        unmetPracticeCount: number;
      };
  overview: CyberAssessmentOverviewSnapshotSelectDto;
  purpleTeamExercises:
    | {
        items: CyberAssessmentPurpleTeamExerciseSelectDto[];
      }
    | CyberAssessmentPurpleTeamExerciseSelectDto[];
  readinessComparison:
    | CyberAssessmentReadinessComparisonSelectDto
    | {
        summary: string;
      };
};

type CyberAssessmentCompletedReportLike = {
  evidencePackChecklist?: CyberAssessmentEvidencePackChecklistSelectDto | null;
  reportSnapshot?: CyberAssessmentReportSnapshotLike | null;
};

export type CyberAssessmentActionPlanItemLike = Partial<CyberAssessmentActionPlanItemSelectDto> &
  Pick<
    CyberAssessmentActionPlanItemSelectDto,
    'phaseTitle' | 'priority' | 'prompt' | 'questionId' | 'recommendedAction'
  >;

export type CyberAssessmentBlockingItemLike = Partial<
  Omit<CyberAssessmentBlockingDeficiencyItemSelectDto, 'stigSeverity'>
> &
  Pick<
    CyberAssessmentBlockingDeficiencyItemSelectDto,
    'explanation' | 'missionImpactIfNo' | 'prompt' | 'questionId' | 'response'
  > & {
    stigSeverity?: string | null;
  };

export type CyberAssessmentReadinessCard = {
  amberCount: number;
  atoPathway: string;
  blockerCount: number;
  classificationCeiling: string;
  classificationCeilingRationale: string;
  deploymentReadinessBand: string;
  evidenceChecklist: string[];
  estimatedRemediationEffort: string;
  jurisdiction: string;
  jurisdictionCode: string;
  keyMissingEvidence: string[];
  keyControls: string[];
  notes: CyberAssessmentReadinessNote[];
  questions: CyberAssessmentJurisdictionQuestionLike[];
};

export type CyberAssessmentReadinessNote = {
  body: string;
  label: string;
};

export type CyberAssessmentAukusGateCard = {
  blockerCount: number;
  blockerQuestionIds: string[];
  classificationGate: string;
  isEligible: boolean;
  minimumCpiBand: number;
};

export type CyberAssessmentCmmcDomainCard = CyberAssessmentCmmcDomainSummaryLike & {
  tone: 'critical' | 'neutral' | 'success' | 'warning';
};

export type CyberAssessmentCmmcGapPriorityCard = CyberAssessmentCmmcGapPriorityLike & {
  rank: number;
};

export type CyberAssessmentCmmcGapPriorityGroupCard = {
  domainName: string;
  items: CyberAssessmentCmmcGapPriorityLike[];
};

export type CyberAssessmentOverviewMetric = {
  description: string;
  tone: 'critical' | 'success' | 'warning';
  value: string;
};

export type CyberAssessmentOverviewJurisdictionBadge = {
  classificationCeiling: string;
  jurisdiction: string;
};

export type CyberAssessmentOverviewSummary = {
  aukusCommonCeiling: CyberAssessmentOverviewMetric & {
    jurisdictionBadges: CyberAssessmentOverviewJurisdictionBadge[];
  };
  aukusReadiness: CyberAssessmentOverviewMetric;
  cmmcIndicativeLevel: CyberAssessmentOverviewMetric;
  cpiPercentage: number;
  deploymentReadiness: CyberAssessmentOverviewMetric;
};

export type CyberAssessmentCompletedReportModel = {
  aukusGateCards: CyberAssessmentAukusGateCard[];
  cmmcDomainCards: CyberAssessmentCmmcDomainCard[];
  cmmcGapPriorities: CyberAssessmentCmmcGapPriorityCard[];
  cmmcGapPriorityGroups: CyberAssessmentCmmcGapPriorityGroupCard[];
  overviewSummary: CyberAssessmentOverviewSummary;
  purpleTeamExerciseItems: CyberAssessmentPurpleTeamExerciseSelectDto[];
  purpleTeamGreenCount: number;
  purpleTeamIssueItems: CyberAssessmentPurpleTeamExerciseSelectDto[];
  readinessCards: CyberAssessmentReadinessCard[];
};

export type CyberAssessmentEvidencePackChecklistJurisdictionCard = {
  checkedItems: number;
  items: CyberAssessmentEvidencePackChecklistItemLike[];
  jurisdiction: string;
  jurisdictionCode: string;
  progress: number;
  totalItems: number;
};

export type CyberAssessmentEvidencePackChecklistModel = {
  checkedItems: number;
  isEmpty: boolean;
  jurisdictions: CyberAssessmentEvidencePackChecklistJurisdictionCard[];
  progress: number;
  totalItems: number;
};

export type CyberAssessmentReportSection =
  | 'actionPlan'
  | 'aukusPillar2'
  | 'blockingDeficiencies'
  | 'cmmc'
  | 'fullReport'
  | 'overview'
  | 'purpleTeamExercises'
  | 'readinessComparison';

const AUKUS_GATE_ORDER = ['SECRET_REL_AUKUS', 'TOP_SECRET_REL_AUKUS'];
export const CYBER_ASSESSMENT_COMPLETED_REPORT_SECTIONS = [
  'overview',
  'blockingDeficiencies',
  'readinessComparison',
  'aukusPillar2',
  'cmmc',
  'actionPlan',
  'purpleTeamExercises',
] as const satisfies readonly Exclude<CyberAssessmentReportSection, 'fullReport'>[];

export const CYBER_ASSESSMENT_REPORT_SECTION_OPTIONS = [
  {
    label: 'Full report',
    value: 'fullReport',
  },
  {
    label: 'Overview',
    value: 'overview',
  },
  {
    label: 'Blocking Deficiencies',
    value: 'blockingDeficiencies',
  },
  {
    label: 'Readiness Comparison',
    value: 'readinessComparison',
  },
  {
    label: 'AUKUS & Pillar II',
    value: 'aukusPillar2',
  },
  {
    label: 'CMMC',
    value: 'cmmc',
  },
  {
    label: 'Action Plan',
    value: 'actionPlan',
  },
  {
    label: 'Purple Team Exercises',
    value: 'purpleTeamExercises',
  },
] as const satisfies readonly {
  label: string;
  value: CyberAssessmentReportSection;
}[];

const JURISDICTION_LABELS: Record<string, string> = {
  AU: 'Australia',
  UK: 'United Kingdom',
  US: 'United States',
};
const JURISDICTION_ORDER = ['AU', 'US', 'UK'];
const UNKNOWN_METRIC = 'N/A';

const formatReadinessNoteLabel = (params: { index: number; note: string }): string => {
  const { index, note } = params;

  if (note.includes('Essential Eight')) {
    return 'Essential Eight Mapping';
  }

  if (note.includes('STIG')) {
    return 'STIG Compliance';
  }

  if (note.includes('JSP 604') || note.includes('Rule 10')) {
    return 'JSP 604 Rule 10';
  }

  if (note.includes('key control')) {
    return 'Key Controls';
  }

  return `Note ${String(index + 1)}`;
};

const formatLabelToken = (value: string): string => {
  if (value === 'AUKUS') {
    return 'AUKUS';
  }

  if (value === 'AI') {
    return 'AI';
  }

  if (value === 'ML') {
    return 'ML';
  }

  return `${value.charAt(0)}${value.slice(1).toLowerCase()}`;
};

export const formatCyberAssessmentReportLabel = (value: string): string => {
  return value
    .split(/[_\s-]+/g)
    .filter(Boolean)
    .map(formatLabelToken)
    .join(' ');
};

export const parseCyberAssessmentReportSection = (value: string): CyberAssessmentReportSection | null => {
  const normalized = value.trim().toLowerCase();

  if (normalized === 'actionplan') return 'actionPlan';
  if (normalized === 'aukuspillar2') return 'aukusPillar2';
  if (normalized === 'blockingdeficiencies') return 'blockingDeficiencies';
  if (normalized === 'cmmc') return 'cmmc';
  if (normalized === 'fullreport') return 'fullReport';
  if (normalized === 'overview') return 'overview';
  if (normalized === 'purpleteamexercises') return 'purpleTeamExercises';
  if (normalized === 'readinesscomparison') return 'readinessComparison';

  return null;
};

export const getVisibleCyberAssessmentReportSections = (
  selectedSection: CyberAssessmentReportSection,
): readonly Exclude<CyberAssessmentReportSection, 'fullReport'>[] => {
  if (selectedSection === 'fullReport') {
    return CYBER_ASSESSMENT_COMPLETED_REPORT_SECTIONS;
  }

  return [selectedSection];
};

const isCyberAssessmentActionPlanItemLike = (value: unknown): value is CyberAssessmentActionPlanItemLike => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return (
    'phaseTitle' in value &&
    typeof value.phaseTitle === 'string' &&
    'priority' in value &&
    typeof value.priority === 'string' &&
    'prompt' in value &&
    typeof value.prompt === 'string' &&
    'questionId' in value &&
    typeof value.questionId === 'string' &&
    'recommendedAction' in value &&
    typeof value.recommendedAction === 'string'
  );
};

const isCyberAssessmentBlockingItemLike = (value: unknown): value is CyberAssessmentBlockingItemLike => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return (
    'explanation' in value &&
    typeof value.explanation === 'string' &&
    'missionImpactIfNo' in value &&
    typeof value.missionImpactIfNo === 'string' &&
    'prompt' in value &&
    typeof value.prompt === 'string' &&
    'questionId' in value &&
    typeof value.questionId === 'string' &&
    'response' in value &&
    typeof value.response === 'string'
  );
};

const normalizeCyberAssessmentActionPlanItem = (
  value: CyberAssessmentActionPlanItemLike,
): CyberAssessmentActionPlanItemLike => {
  return {
    ...value,
    blockingType: value.blockingType ?? CyberAssessmentBlockingType.ANSWER_NO,
    evidenceToAttach: value.evidenceToAttach ?? [],
    gateTags: value.gateTags ?? [],
    isDeploymentBlocker: value.isDeploymentBlocker ?? false,
    jurisdictions: value.jurisdictions ?? [],
    missionImpactIfNo: value.missionImpactIfNo ?? '',
    section: value.section ?? 'General',
    stageLabel: value.stageLabel ?? 'Deployment',
    whatGoodLooksLike: value.whatGoodLooksLike ?? value.recommendedAction,
  };
};

const normalizeCyberAssessmentBlockingItem = (
  value: CyberAssessmentBlockingItemLike,
): CyberAssessmentBlockingItemLike => {
  return {
    ...value,
    blockingType: value.blockingType ?? CyberAssessmentBlockingType.ANSWER_NO,
    gateTags: value.gateTags ?? [],
    isDeploymentBlocker: value.isDeploymentBlocker ?? false,
    jurisdictions: value.jurisdictions ?? [],
    section: value.section ?? 'General',
  };
};

const formatPercentage = (value: number): number => {
  return Math.round(value * 100);
};

const resolveAukusCommonCeilingTone = (classificationCeiling?: string | null): 'critical' | 'success' | 'warning' => {
  if (!classificationCeiling) {
    return 'critical';
  }

  if (classificationCeiling.includes('TOP SECRET')) {
    return 'success';
  }

  if (classificationCeiling.includes('SECRET')) {
    return 'warning';
  }

  return 'critical';
};

const resolveBandTone = (value?: string | null): 'critical' | 'success' | 'warning' => {
  const normalizedValue = value?.trim().toUpperCase();

  if (!normalizedValue) {
    return 'critical';
  }

  if (['CLEARED', 'L2 READY', 'L3 READY', 'READY'].includes(normalizedValue)) {
    return 'success';
  }

  if (['CONDITIONAL', 'PARTIAL', 'PROVISIONAL'].includes(normalizedValue)) {
    return 'warning';
  }

  return 'critical';
};

const buildCyberAssessmentOverviewSummary = (params: {
  aukusPillar2: CyberAssessmentReportSnapshotLike['aukusPillar2'];
  cmmc: CyberAssessmentReportSnapshotLike['cmmc'];
  overview: CyberAssessmentReportSnapshotLike['overview'];
  readinessJurisdictions: CyberAssessmentJurisdictionReadinessLike[];
}): CyberAssessmentOverviewSummary => {
  const { aukusPillar2, cmmc, overview, readinessJurisdictions } = params;
  const jurisdictionBadges = readinessJurisdictions
    .sort(
      (left, right) => JURISDICTION_ORDER.indexOf(left.jurisdiction) - JURISDICTION_ORDER.indexOf(right.jurisdiction),
    )
    .map((jurisdiction) => ({
      classificationCeiling: jurisdiction.classificationCeiling,
      jurisdiction: jurisdiction.jurisdiction,
    }));
  const cmmcPracticeCount =
    'practicesAssessed' in cmmc && typeof cmmc.practicesAssessed === 'number'
      ? cmmc.practicesAssessed
      : 'practicesMet' in cmmc &&
          'practicesNotAssessed' in cmmc &&
          'practicesNotMet' in cmmc &&
          'practicesPartial' in cmmc
        ? cmmc.practicesMet + cmmc.practicesNotAssessed + cmmc.practicesNotMet + cmmc.practicesPartial
        : 0;
  const cmmcMetRatio =
    'practicesMet' in cmmc && cmmcPracticeCount > 0 ? formatPercentage(cmmc.practicesMet / cmmcPracticeCount) : 0;
  const cmmcIndicativeValue =
    'indicativeLevel' in cmmc && typeof cmmc.indicativeLevel === 'string' ? cmmc.indicativeLevel : 'Unrated';
  const aukusLowestCommonClassificationCeiling =
    'lowestCommonClassificationCeiling' in aukusPillar2
      ? aukusPillar2.lowestCommonClassificationCeiling
      : UNKNOWN_METRIC;
  const aukusReadinessBand =
    'readinessBand' in aukusPillar2 ? aukusPillar2.readinessBand || UNKNOWN_METRIC : UNKNOWN_METRIC;
  const interoperabilityBlockerCount =
    'interoperabilityBlockers' in aukusPillar2 ? aukusPillar2.interoperabilityBlockers.length : 0;
  const deploymentReadinessBand = overview.deploymentReadinessBand || UNKNOWN_METRIC;
  const cmmcNotAssessedCount =
    typeof cmmc === 'object' && cmmc !== null && 'practicesNotAssessed' in cmmc
      ? (typeof cmmc.practicesGapMapped === 'number' ? cmmc.practicesGapMapped : 0) +
        (typeof cmmc.practicesNotAssessed === 'number' ? cmmc.practicesNotAssessed : 0)
      : 0;

  return {
    aukusCommonCeiling: {
      description: 'Lowest across AU / US / UK readiness outputs',
      jurisdictionBadges,
      tone: resolveAukusCommonCeilingTone(aukusLowestCommonClassificationCeiling),
      value: aukusLowestCommonClassificationCeiling,
    },
    aukusReadiness: {
      description: `${interoperabilityBlockerCount} interoperability blockers remain`,
      tone: resolveBandTone(aukusReadinessBand),
      value: aukusReadinessBand,
    },
    cmmcIndicativeLevel: {
      description:
        'level2CoveragePercentage' in cmmc && 'level3CoveragePercentage' in cmmc
          ? [
              `L2: ${cmmc.level2CoveragePercentage}%`,
              `L3: ${cmmc.level3CoveragePercentage}%`,
              cmmcNotAssessedCount > 0 ? `Not Assessed: ${cmmcNotAssessedCount}` : '',
            ]
              .filter(Boolean)
              .join(' • ')
          : cmmcPracticeCount > 0
            ? `Met ${cmmcMetRatio}% of target practices`
            : 'No CMMC practice data available',
      tone:
        cmmcIndicativeValue === 'L3 Ready' || cmmcIndicativeValue === 'L2 Ready'
          ? 'success'
          : cmmcMetRatio >= 50
            ? 'warning'
            : 'critical',
      value: cmmcIndicativeValue,
    },
    cpiPercentage: formatPercentage(overview.cpiRatio),
    deploymentReadiness: {
      description: `${overview.assessmentCompletionPercentage}% assessment completion`,
      tone: resolveBandTone(deploymentReadinessBand),
      value: deploymentReadinessBand,
    },
  };
};

export const getCyberAssessmentActionPlanItems = (
  cyberAssessment: CyberAssessmentCompletedReportLike,
): CyberAssessmentActionPlanItemLike[] => {
  const actionPlan = cyberAssessment.reportSnapshot?.actionPlan;

  if (Array.isArray(actionPlan)) {
    return actionPlan.filter(isCyberAssessmentActionPlanItemLike).map(normalizeCyberAssessmentActionPlanItem);
  }

  if (actionPlan && 'items' in actionPlan && Array.isArray(actionPlan.items)) {
    return actionPlan.items.filter(isCyberAssessmentActionPlanItemLike).map(normalizeCyberAssessmentActionPlanItem);
  }

  return [];
};

export const getCyberAssessmentBlockingItems = (
  cyberAssessment: CyberAssessmentCompletedReportLike,
): CyberAssessmentBlockingItemLike[] => {
  const blockingDeficiencies = cyberAssessment.reportSnapshot?.blockingDeficiencies;

  if (Array.isArray(blockingDeficiencies)) {
    return blockingDeficiencies.filter(isCyberAssessmentBlockingItemLike).map(normalizeCyberAssessmentBlockingItem);
  }

  if (blockingDeficiencies && 'items' in blockingDeficiencies && Array.isArray(blockingDeficiencies.items)) {
    return blockingDeficiencies.items
      .filter(isCyberAssessmentBlockingItemLike)
      .map(normalizeCyberAssessmentBlockingItem);
  }

  return [];
};

function resolveCmmcDomainTone(
  domainSummary: CyberAssessmentCmmcDomainSummaryLike,
): CyberAssessmentCmmcDomainCard['tone'] {
  if (domainSummary.practicesNotMet > 0) return 'critical';
  if (domainSummary.practicesPartial > 0) return 'warning';
  if (domainSummary.practicesGapMapped > 0 || domainSummary.practicesPartiallyMapped > 0) return 'warning';
  if (domainSummary.practicesMet > 0) return 'success';
  return 'neutral';
}

export const buildCyberAssessmentCompletedReportModel = (
  cyberAssessment: CyberAssessmentCompletedReportLike,
): CyberAssessmentCompletedReportModel => {
  const reportSnapshot = cyberAssessment.reportSnapshot;

  if (!reportSnapshot) {
    return {
      aukusGateCards: [],
      cmmcDomainCards: [],
      cmmcGapPriorities: [],
      cmmcGapPriorityGroups: [],
      overviewSummary: {
        aukusCommonCeiling: {
          description: '',
          jurisdictionBadges: [],
          tone: 'critical',
          value: UNKNOWN_METRIC,
        },
        aukusReadiness: {
          description: '',
          tone: 'critical',
          value: UNKNOWN_METRIC,
        },
        cmmcIndicativeLevel: {
          description: '',
          tone: 'critical',
          value: UNKNOWN_METRIC,
        },
        cpiPercentage: 0,
        deploymentReadiness: {
          description: '',
          tone: 'critical',
          value: UNKNOWN_METRIC,
        },
      },
      purpleTeamExerciseItems: [],
      purpleTeamGreenCount: 0,
      purpleTeamIssueItems: [],
      readinessCards: [],
    };
  }

  const readinessJurisdictions =
    'jurisdictions' in reportSnapshot.readinessComparison &&
    Array.isArray(reportSnapshot.readinessComparison.jurisdictions)
      ? reportSnapshot.readinessComparison.jurisdictions
      : [];
  const aukusGateResults =
    'gateResults' in reportSnapshot.aukusPillar2 && Array.isArray(reportSnapshot.aukusPillar2.gateResults)
      ? reportSnapshot.aukusPillar2.gateResults
      : [];
  const cmmcDomainSummaries =
    'domainSummaries' in reportSnapshot.cmmc && Array.isArray(reportSnapshot.cmmc.domainSummaries)
      ? reportSnapshot.cmmc.domainSummaries
      : [];
  const cmmcGapPriorities =
    'gapRemediationPriorities' in reportSnapshot.cmmc && Array.isArray(reportSnapshot.cmmc.gapRemediationPriorities)
      ? reportSnapshot.cmmc.gapRemediationPriorities.map((gapPriority, index) => ({
          ...gapPriority,
          rank: index + 1,
        }))
      : [];
  const cmmcGapPriorityGroups =
    'gapRemediationPriorityGroups' in reportSnapshot.cmmc &&
    Array.isArray(reportSnapshot.cmmc.gapRemediationPriorityGroups)
      ? reportSnapshot.cmmc.gapRemediationPriorityGroups
      : cmmcGapPriorities
          .filter((gapPriority) => gapPriority.status === 'NOT_MET')
          .reduce<CyberAssessmentCmmcGapPriorityGroupCard[]>((groups, gapPriority) => {
            const existingGroup = groups.find((group) => group.domainName === gapPriority.domainName);

            if (existingGroup) {
              existingGroup.items.push(gapPriority);
              return groups;
            }

            groups.push({
              domainName: gapPriority.domainName,
              items: [gapPriority],
            });

            return groups;
          }, []);
  const purpleTeamExerciseItems = Array.isArray(reportSnapshot.purpleTeamExercises)
    ? reportSnapshot.purpleTeamExercises
    : Array.isArray(reportSnapshot.purpleTeamExercises?.items)
      ? reportSnapshot.purpleTeamExercises.items
      : [];

  const readinessCards = [...readinessJurisdictions]
    .sort(
      (left, right) => JURISDICTION_ORDER.indexOf(left.jurisdiction) - JURISDICTION_ORDER.indexOf(right.jurisdiction),
    )
    .map((jurisdiction) => ({
      amberCount: jurisdiction.amberQuestions.length,
      atoPathway: jurisdiction.atoPathway,
      blockerCount: jurisdiction.blockingQuestions.length,
      classificationCeiling: jurisdiction.classificationCeiling,
      classificationCeilingRationale: jurisdiction.classificationCeilingRationale,
      deploymentReadinessBand: jurisdiction.deploymentReadinessBand,
      evidenceChecklist: jurisdiction.evidenceChecklist ?? [],
      estimatedRemediationEffort: jurisdiction.estimatedRemediationEffort,
      jurisdiction: JURISDICTION_LABELS[jurisdiction.jurisdiction] ?? jurisdiction.jurisdiction,
      jurisdictionCode: jurisdiction.jurisdiction,
      keyMissingEvidence: jurisdiction.keyMissingEvidence,
      keyControls: jurisdiction.keyControls ?? [],
      notes: (jurisdiction.notes ?? []).map((note, index) => ({
        body: note,
        label: formatReadinessNoteLabel({
          index,
          note,
        }),
      })),
      questions: [...jurisdiction.blockingQuestions, ...jurisdiction.amberQuestions],
    }));
  const aukusGateCards = [...aukusGateResults]
    .sort(
      (left, right) =>
        AUKUS_GATE_ORDER.indexOf(left.classificationGate) - AUKUS_GATE_ORDER.indexOf(right.classificationGate),
    )
    .map((gateResult) => ({
      blockerCount: gateResult.blockerQuestionIds.length,
      blockerQuestionIds: gateResult.blockerQuestionIds,
      classificationGate: formatCyberAssessmentReportLabel(gateResult.classificationGate),
      isEligible: gateResult.isEligible,
      minimumCpiBand: gateResult.minimumCpiBand,
    }));
  const cmmcDomainCards = [...cmmcDomainSummaries]
    .sort((left, right) => {
      return (
        right.practicesNotMet - left.practicesNotMet ||
        right.practicesPartial - left.practicesPartial ||
        right.practicesPartiallyMapped - left.practicesPartiallyMapped ||
        right.practicesGapMapped - left.practicesGapMapped ||
        left.domainName.localeCompare(right.domainName)
      );
    })
    .map(
      (domainSummary) =>
        ({
          ...domainSummary,
          tone: resolveCmmcDomainTone(domainSummary),
        }) satisfies CyberAssessmentCmmcDomainCard,
    );

  return {
    aukusGateCards,
    cmmcDomainCards,
    cmmcGapPriorities,
    cmmcGapPriorityGroups,
    overviewSummary: buildCyberAssessmentOverviewSummary({
      aukusPillar2: reportSnapshot.aukusPillar2,
      cmmc: reportSnapshot.cmmc,
      overview: reportSnapshot.overview,
      readinessJurisdictions,
    }),
    purpleTeamExerciseItems,
    purpleTeamGreenCount: purpleTeamExerciseItems.filter((item) => item.ragStatus === 'GREEN').length,
    purpleTeamIssueItems: purpleTeamExerciseItems.filter((item) => item.ragStatus !== 'GREEN'),
    readinessCards,
  };
};

export const buildCyberAssessmentEvidencePackChecklistModel = (
  cyberAssessment: CyberAssessmentCompletedReportLike,
): CyberAssessmentEvidencePackChecklistModel => {
  const checklistJurisdictions = cyberAssessment.evidencePackChecklist?.jurisdictions ?? [];
  const jurisdictions = [...checklistJurisdictions]
    .sort(
      (left, right) => JURISDICTION_ORDER.indexOf(left.jurisdiction) - JURISDICTION_ORDER.indexOf(right.jurisdiction),
    )
    .map((jurisdiction) => {
      const checkedItems = jurisdiction.items.filter((item) => item.checked).length;
      const totalItems = jurisdiction.items.length;

      return {
        checkedItems,
        items: jurisdiction.items,
        jurisdiction: JURISDICTION_LABELS[jurisdiction.jurisdiction] ?? jurisdiction.jurisdiction,
        jurisdictionCode: jurisdiction.jurisdiction,
        progress: totalItems === 0 ? 0 : checkedItems / totalItems,
        totalItems,
      } satisfies CyberAssessmentEvidencePackChecklistJurisdictionCard;
    });
  const checkedItems = jurisdictions.reduce((total, jurisdiction) => total + jurisdiction.checkedItems, 0);
  const totalItems = jurisdictions.reduce((total, jurisdiction) => total + jurisdiction.totalItems, 0);

  return {
    checkedItems,
    isEmpty: totalItems === 0,
    jurisdictions,
    progress: totalItems === 0 ? 0 : checkedItems / totalItems,
    totalItems,
  };
};
